import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';

const GAP_BELOW_ANCHOR = 10;

const ToastButton = styled(motion.button)`
  z-index: 2100;
  max-width: min(380px, calc(100vw - 32px));
  padding: 12px 20px;
  border-radius: 999px;
  background: linear-gradient(135deg, #c4007a 0%, #0099b8 100%);
  border: 1px solid rgba(255, 238, 0, 0.55);
  color: #fff;
  font-size: 0.9rem;
  font-weight: 600;
  text-align: center;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.45);
  cursor: pointer;
  transition: border-color 0.2s ease, transform 0.2s ease;

  ${(props) =>
    props.$anchored
      ? css`
          position: fixed;
          transform: translate(-50%, 0);

          &:hover {
            border-color: rgba(0, 245, 255, 0.7);
            transform: translate(-50%, -2px);
          }

          &:active {
            transform: translate(-50%, 0);
          }
        `
      : css`
          position: fixed;
          bottom: 24px;
          left: 50%;
          transform: translateX(-50%);

          &:hover {
            border-color: rgba(0, 245, 255, 0.7);
            transform: translateX(-50%) translateY(-2px);
          }

          &:active {
            transform: translateX(-50%) translateY(0);
          }
        `}
`;

function SaveToast({ message, onAction, onDismiss, anchorEl }) {
  const buttonRef = useRef(null);
  const [anchorPosition, setAnchorPosition] = useState(null);

  useLayoutEffect(() => {
    if (!message || !anchorEl) {
      setAnchorPosition(null);
      return undefined;
    }

    const updatePosition = () => {
      const rect = anchorEl.getBoundingClientRect();
      setAnchorPosition({
        top: rect.bottom + GAP_BELOW_ANCHOR,
        left: rect.left + rect.width / 2
      });
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    const observer = new ResizeObserver(updatePosition);
    observer.observe(anchorEl);

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
      observer.disconnect();
      setAnchorPosition(null);
    };
  }, [message, anchorEl]);

  useEffect(() => {
    if (!message) return undefined;

    const handlePointerDown = (event) => {
      if (buttonRef.current?.contains(event.target)) return;
      if (anchorEl?.contains(event.target)) return;
      onDismiss?.();
    };

    document.addEventListener('pointerdown', handlePointerDown, true);
    return () => document.removeEventListener('pointerdown', handlePointerDown, true);
  }, [message, onDismiss, anchorEl]);

  const anchored = !!anchorPosition;

  return (
    <AnimatePresence>
      {message && (
        <ToastButton
          ref={buttonRef}
          type="button"
          $anchored={anchored}
          onClick={onAction}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 6 }}
          transition={{ duration: 0.2 }}
          style={
            anchored
              ? {
                  top: anchorPosition.top,
                  left: anchorPosition.left
                }
              : undefined
          }
        >
          {message}
        </ToastButton>
      )}
    </AnimatePresence>
  );
}

export default SaveToast;
